import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BellIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';

export const NotificationPopup: React.FC = () => {
  const [showPopup, setShowPopup] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    // Check for unread notifications on mount
    const checkNotifications = () => {
      const authToken = localStorage.getItem('authToken');
      
      if (!authToken) return; // Don't show if not logged in
      
      // Check if popup was already shown today
      const lastShown = localStorage.getItem('hasSeenNotificationPopup');
      const today = new Date().toDateString();
      
      if (lastShown === today) return; // Already shown today
      
      // Simulate unread notifications count (in real app, fetch from API)
      const mockUnreadCount = 3;
      setUnreadCount(mockUnreadCount);
      
      // Show popup if user has unread notifications
      if (mockUnreadCount > 0) {
        // Delay showing popup after page load
        const timer = setTimeout(() => {
          setShowPopup(true);
        }, 3000); // Show after 3 seconds
        
        return () => clearTimeout(timer);
      }
    };

    checkNotifications();
  }, []);

  const handleClose = () => {
    setShowPopup(false);
    // Mark as seen for today
    const today = new Date().toDateString();
    localStorage.setItem('hasSeenNotificationPopup', today);
  };

  const handleViewNotifications = () => {
    handleClose();
    navigate('/notifications');
  };

  return (
    <AnimatePresence>
      {showPopup && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-25 z-50 flex items-start justify-end p-4 pt-20"
            onClick={handleClose}
          >
            {/* Popup Card */}
            <motion.div
              initial={{ opacity: 0, y: -20, x: 20 }}
              animate={{ opacity: 1, y: 0, x: 0 }}
              exit={{ opacity: 0, y: -20, x: 20 }}
              transition={{ type: 'spring', duration: 0.5 }}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-sm w-full border border-gray-200 dark:border-gray-700 overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header with gradient */}
              <div className="bg-gradient-to-r from-primary-500 to-primary-600 p-4 text-white">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="bg-white bg-opacity-20 p-2 rounded-lg">
                      <BellIcon className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">New Notifications</h3>
                      <p className="text-sm text-primary-100">You have {unreadCount} unread notifications</p>
                    </div>
                  </div>
                  <button
                    onClick={handleClose}
                    className="text-white hover:text-gray-200 transition-colors"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="p-4">
                <div className="space-y-3">
                  {/* Sample notification previews */}
                  <div className="flex items-start gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <div className="flex-shrink-0 w-2 h-2 mt-2 bg-blue-600 rounded-full"></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        RTI Application Approved
                      </p>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                        Your application #1247 has been approved
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <div className="flex-shrink-0 w-2 h-2 mt-2 bg-blue-600 rounded-full"></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        Response Received
                      </p>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                        Public Works Department responded to RTI #1245
                      </p>
                    </div>
                  </div>

                  <div className="flex items-start gap-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
                    <div className="flex-shrink-0 w-2 h-2 mt-2 bg-yellow-600 rounded-full"></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        Deadline Approaching
                      </p>
                      <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                        RTI #1243 deadline is in 3 days
                      </p>
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div className="mt-4 flex gap-2">
                  <button
                    onClick={handleClose}
                    className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors text-sm font-medium"
                  >
                    Later
                  </button>
                  <button
                    onClick={handleViewNotifications}
                    className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium shadow-md hover:shadow-lg"
                  >
                    View All
                  </button>
                </div>
              </div>

              {/* Footer note */}
              <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700">
                <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                  Click on the bell icon anytime to view your notifications
                </p>
              </div>
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
